import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { psAPI } from '../../../services/api';
import { toast } from 'sonner';
import {
  Lock,
  Unlock,
  Plus,
  RefreshCw,
  Calendar,
  CheckCircle
} from 'lucide-react';

export default function PeriodLocks() {
  const [locks, setLocks] = useState([]);
  const { canWrite } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [filterProject, setFilterProject] = useState('__all__');
  const [filterYear, setFilterYear] = useState(new Date().getFullYear().toString());

  const [formData, setFormData] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    period: 1,
    lock_costs: true,
    lock_commitments: true,
    lock_planning: true,
    lock_budgeting: true,
    reason: ''
  });


  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [locksRes, projectsRes] = await Promise.all([
        psAPI.getPeriodLocks({ 
          project_id: filterProject !== '__all__' ? filterProject : undefined,
          fiscal_year: filterYear ? parseInt(filterYear) : undefined
        }),
        psAPI.getProjects()
      ]);
      setLocks(locksRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterProject, filterYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return;
    }
    try {
      await psAPI.createPeriodLock(formData);
      toast.success('Period locked successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create period lock');
    }
  };

  const handleUnlock = async (lockId) => {
    try {
      await psAPI.unlockPeriod(lockId);
      toast.success('Period unlocked');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock period');
    }
  };

  const handleClose = async (lockId) => {
    if (!window.confirm('Are you sure you want to close this period? This action cannot be undone.')) return;
    try {
      await psAPI.closePeriod(lockId);
      toast.success('Period closed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close period');
    }
  };

  const resetForm = () => {
    setFormData({
      project_id: '',
      fiscal_year: new Date().getFullYear(),
      period: 1,
      lock_costs: true,
      lock_commitments: true,
      lock_planning: true,
      lock_budgeting: true,
      reason: ''
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      OPEN: 'bg-emerald-100 text-emerald-700',
      LOCKED: 'bg-amber-100 text-amber-700',
      CLOSED: 'bg-orange-100 text-orange-700'
    };
    return <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>{status}</Badge>;
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  const periods = Array.from({ length: 12 }, (_, i) => i + 1);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Period Locks</h1>
            <p className="text-gray-500">Manage period locks for project closing</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {canWrite('project_system') && (
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-lock-btn">
              <Plus className="w-4 h-4 mr-2" />
              Lock Period
            </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <Label>Project</Label>
                <Select value={filterProject} onValueChange={setFilterProject}>
                  <SelectTrigger data-testid="filter-project">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All Projects</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-48">
                <Label>Fiscal Year</Label>
                <Select value={filterYear} onValueChange={setFilterYear}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Locks Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-orange-600" />
              Period Locks ({locks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-orange-600" />
              </div>
            ) : locks.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3">Project</th>
                      <th className="text-left p-3">Year</th>
                      <th className="text-left p-3">Period</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Locked At</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {locks.map((lock) => (
                      <tr key={lock.lock_id} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-medium">{lock.project_name || lock.project_id}</td>
                        <td className="p-3">{lock.fiscal_year}</td>
                        <td className="p-3">{lock.period}</td>
                        <td className="p-3">{getStatusBadge(lock.status)}</td>
                        <td className="p-3 text-sm text-gray-500">
                          {lock.locked_at ? new Date(lock.locked_at).toLocaleString() : '-'}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {lock.status === 'LOCKED' && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleUnlock(lock.lock_id)}
                                  data-testid={`unlock-${lock.lock_id}`}
                                >
                                  <Unlock className="w-3 h-3 mr-1" />
                                  Unlock
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="default"
                                  onClick={() => handleClose(lock.lock_id)}
                                  data-testid={`close-${lock.lock_id}`}
                                >
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Close
                                </Button>
                              </>
                            )}
                            {lock.status === 'OPEN' && (
                              <Badge className="bg-emerald-100 text-emerald-700">Open for posting</Badge>
                            )}
                            {lock.status === 'CLOSED' && (
                              <Badge className="bg-orange-100 text-orange-700">Period closed</Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No period locks found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Lock Period</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              <Select value={formData.project_id} onValueChange={(v) => setFormData({ ...formData, project_id: v })}>
                <SelectTrigger data-testid="select-project">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fiscal Year</Label>
                <Select 
                  value={formData.fiscal_year.toString()} 
                  onValueChange={(v) => setFormData({ ...formData, fiscal_year: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Period</Label>
                <Select 
                  value={formData.period.toString()} 
                  onValueChange={(v) => setFormData({ ...formData, period: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map(p => (
                      <SelectItem key={p} value={p.toString()}>Period {p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Reason</Label>
              <Input
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Reason for locking"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!formData.project_id} data-testid="submit-lock">
              Lock Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
